/**
 * Local synthesis of `InspectionData` from the client-side world model.
 *
 * Why: SERVER's P1.5 Gateway (which produces the authoritative
 * `InspectionPayload`) isn't live yet. For offline-dev we derive a
 * payload shape that matches the wire schema exactly, so the panel
 * renderers are final-quality against real data when the server
 * round-trip arrives. The only thing that changes in that transition
 * is which side of the wire the data comes from.
 *
 * Shape: identical to `InspectionData` from `wire/types` (ts-rs-
 * generated from the Rust side). The Phase 2+ SomaInspection body
 * wraps Avatar per CLIENT_DESIGN §14.5 — a no-op stub here.
 */

import type {
  AvatarInspection,
  BuildingInspection,
  InspectionData,
  InspectTarget,
  PropInspection,
  TileInspection,
} from '../wire';
import type { WorldModel } from '../world';
import { tileIndex } from '../world';

/**
 * Synthesise a plausible `InspectionPayload` for `target` from the local
 * world model. Returns `null` for targets the model has no data for.
 */
export function deriveInspection(
  model: WorldModel,
  target: InspectTarget,
): InspectionData | null {
  if ('Tile' in target) return deriveTile(model, target.Tile);
  if ('Prop' in target) return deriveProp(model, target.Prop);
  if ('Building' in target) return deriveBuilding(model, target.Building);
  if ('Player' in target) return deriveAvatar(model, target.Player);
  if ('Soma' in target) return null; // Phase 2+
  return null;
}

function deriveTile(
  model: WorldModel,
  pos: [number, number],
): InspectionData | null {
  const [x, y] = pos;
  if (x < 0 || x >= model.width || y < 0 || y >= model.height) return null;
  const idx = tileIndex(x, y, model.width);
  const material = model.tileMaterials[idx] ?? 0;
  const roomId = model.tileRoomIds[idx] ?? -1;
  const body: TileInspection = {
    position: [x, y],
    material,
    elevation: model.tileElevations[idx] ?? 0,
    room: roomId >= 0 ? roomId : null,
    indoors: roomId >= 0,
  };
  return { Tile: body };
}

function deriveProp(
  model: WorldModel,
  id: number,
): InspectionData | null {
  const prop = model.props.get(id);
  if (!prop) return null;
  const body: PropInspection = {
    id: prop.id,
    kind: prop.kind,
    position: prop.position,
    harvests_remaining: null, // Phase 1: not yet populated
  };
  return { Prop: body };
}

function deriveBuilding(
  model: WorldModel,
  id: number,
): InspectionData | null {
  // Phase 1 conflates buildings with rooms — the client has no
  // building-level aggregation yet (that's WORLDGEN's responsibility).
  // If the id matches a known room, synthesise from that.
  const room = model.rooms.get(id);
  if (!room || room.tiles.length === 0) return null;
  // Centre = midpoint of the tile bounding box.
  let minX = Infinity,
    maxX = -Infinity,
    minY = Infinity,
    maxY = -Infinity;
  for (const [x, y] of room.tiles) {
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  const body: BuildingInspection = {
    id: room.id,
    archetype: room.archetype,
    center: [Math.floor((minX + maxX) / 2), Math.floor((minY + maxY) / 2)],
    owner: null,
  };
  return { Building: body };
}

function deriveAvatar(
  model: WorldModel,
  playerId: bigint,
): InspectionData | null {
  const player = model.players.get(playerId);
  if (!player) return null;
  const body: AvatarInspection = {
    entity: { Player: player.id },
    position: player.position,
    action_status: 'Idle', // Phase 1: no live action-status stream yet
    role: player.role,
    connection: player.connected ? 'Connected' : 'Disconnected',
    display_name: player.display_name,
    discriminator: null,
  };
  return { Avatar: body };
}

/**
 * Hit-test a world-space tile coordinate against the model and return
 * the highest-priority inspect target. Priority order (CLIENT_DESIGN
 * §14.5 click dispatch):
 *   1. Avatar at the tile
 *   2. Prop at the tile
 *   3. Building/room (if tile has room_id)
 *   4. The tile itself
 */
export function hitTestTile(
  model: WorldModel,
  tileX: number,
  tileY: number,
): InspectTarget {
  // Avatar first.
  for (const player of model.players.values()) {
    const [px, py] = player.position;
    if (px === tileX && py === tileY) {
      return { Player: player.id };
    }
  }
  // Prop next.
  for (const prop of model.props.values()) {
    const [px, py] = prop.position;
    if (px === tileX && py === tileY) {
      return { Prop: prop.id };
    }
  }
  // Building / room.
  if (tileX >= 0 && tileX < model.width && tileY >= 0 && tileY < model.height) {
    const roomId = model.tileRoomIds[tileIndex(tileX, tileY, model.width)] ?? -1;
    if (roomId >= 0) return { Building: roomId };
  }
  // Fallback: the tile itself.
  return { Tile: [tileX, tileY] };
}

/**
 * Hit-test a world-space tile coordinate and return **all** entities at that
 * tile, in the same priority order as `hitTestTile` (avatars → props →
 * building → tile). The tile itself is always included as the final entry.
 * Used by the context menu to build a full list of interactable targets.
 */
export function hitTestTileAll(
  model: WorldModel,
  tileX: number,
  tileY: number,
): InspectTarget[] {
  const targets: InspectTarget[] = [];

  for (const player of model.players.values()) {
    const [px, py] = player.position;
    if (px === tileX && py === tileY) {
      targets.push({ Player: player.id });
    }
  }

  for (const prop of model.props.values()) {
    const [px, py] = prop.position;
    if (px === tileX && py === tileY) {
      targets.push({ Prop: prop.id });
    }
  }

  if (tileX >= 0 && tileX < model.width && tileY >= 0 && tileY < model.height) {
    const roomId = model.tileRoomIds[tileIndex(tileX, tileY, model.width)] ?? -1;
    if (roomId >= 0) {
      targets.push({ Building: roomId });
    }
  }

  targets.push({ Tile: [tileX, tileY] });

  return targets;
}
